import { GlButton, GlIcon, GlModal, GlDisclosureDropdownItem, GlTooltipDirective, GlToastMixin } from '@gitlab/ui';
import { visitUrl } from '~/lib/utils/url_utility';
import { __, s__, sprintf } from '~/locale';
import { isMetaClick } from '~/lib/utils/common_utils';
import { MR_WORK_ITEM_RELATIONSHIP_TYPES } from '~/sidebar/constants';
import { newWorkItemPath, canRouterNav, getDraftWorkItemType, lowercaseWorkItemType } from '~/work_items/utils';
import { RELATED_ITEM_ID_URL_QUERY_PARAM, ROUTES, WORK_ITEM_TYPE_NAME_INCIDENT, WORK_ITEM_TYPE_ROUTE_WORK_ITEM } from '../constants';
import CreateWorkItem from './create_work_item.vue';
import CreateWorkItemCancelConfirmationModal from './create_work_item_cancel_confirmation_modal.vue';
export default {
    name: 'CreateWorkItemModal',
    components: {
        CreateWorkItem,
        CreateWorkItemCancelConfirmationModal,
        GlButton,
        GlIcon,
        GlModal,
        GlDisclosureDropdownItem
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        GlToastMixin
    ],
    props: {
        confidential: {
            type: Boolean,
            required: false,
            default: false
        },
        creationContext: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: false,
            default: ''
        },
        fullPath: {
            type: String,
            required: true
        },
        hideButton: {
            type: Boolean,
            required: false,
            default: false
        },
        hideNamespaceSelector: {
            type: Boolean,
            required: false,
            default: false
        },
        hideTypeSelector: {
            type: Boolean,
            required: false,
            default: false
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        parentId: {
            type: String,
            required: false,
            default: ''
        },
        title: {
            type: String,
            required: false,
            default: ''
        },
        visible: {
            type: Boolean,
            required: false,
            default: false
        },
        preselectedWorkItemType: {
            type: String,
            required: false,
            default: null
        },
        asDropdownItem: {
            type: Boolean,
            required: false,
            default: false
        },
        relatedItem: {
            type: Object,
            required: false,
            validator: (i)=>i.id && i.type && i.reference && i.webUrl,
            default: null
        },
        mergeRequestId: {
            type: String,
            required: false,
            default: null
        },
        mergeRequestLinkType: {
            type: String,
            required: false,
            default: null
        },
        mergeRequestTitle: {
            type: String,
            required: false,
            default: ''
        },
        mergeRequestReference: {
            type: String,
            required: false,
            default: ''
        },
        allowProjectsOnly: {
            type: Boolean,
            required: false,
            default: false
        },
        createSource: {
            type: String,
            required: false,
            default: null
        },
        suppressCreatedToast: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'hide-modal',
        'work-item-created'
    ],
    data () {
        const draftWorkItemType = getDraftWorkItemType({
            fullPath: this.fullPath,
            context: this.creationContext,
            relatedItemId: this.relatedItem?.id
        })?.name;
        return {
            isCreateModalVisible: false,
            isConfirmationModalVisible: false,
            selectedWorkItemTypeName: draftWorkItemType || this.preselectedWorkItemType,
            shouldDiscardDraft: false
        };
    },
    computed: {
        useVueRouter () {
            return !this.asDropdownItem && this.$router && this.$router.options.routes?.some((route)=>route.name === 'workItem');
        },
        newWorkItemPathQuery () {
            let query = '';
            let previousQueryParam = false;
            // Only add query string if there's a work item type selected
            if (this.selectedWorkItemTypeName && this.useVueRouter) {
                query += previousQueryParam ? '&' : '?';
                // eslint-disable-next-line @gitlab/require-i18n-strings
                query += `type=${this.selectedWorkItemTypeName}`;
                previousQueryParam = true;
            }
            if (this.relatedItem) {
                query += previousQueryParam ? '&' : '?';
                query += `${RELATED_ITEM_ID_URL_QUERY_PARAM}=${this.relatedItem.id}`;
                previousQueryParam = true;
            }
            query += previousQueryParam ? '&' : '?';
            query += `initialCreationContext=${this.creationContext}`;
            return query;
        },
        newWorkItemPath () {
            return newWorkItemPath({
                fullPath: this.fullPath,
                isGroup: this.isGroup,
                query: this.newWorkItemPathQuery
            });
        },
        newWorkItemText () {
            return sprintf(s__('WorkItem|New %{workItemType}'), {
                workItemType: lowercaseWorkItemType(this.selectedWorkItemTypeName)
            });
        },
        showMergeRequestRelationshipNote () {
            return Boolean(this.mergeRequestLinkType && this.mergeRequestTitle);
        },
        mergeRequestRelationshipIntro () {
            if (this.mergeRequestLinkType === MR_WORK_ITEM_RELATIONSHIP_TYPES.closing) {
                return s__('WorkItem|Item will be closed by:');
            }
            if (this.mergeRequestLinkType === MR_WORK_ITEM_RELATIONSHIP_TYPES.related) {
                return s__('WorkItem|Item will be related to:');
            }
            return '';
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler (visible) {
                this.isCreateModalVisible = visible;
            }
        }
    },
    methods: {
        hideCreateModal () {
            this.$emit('hide-modal');
            this.isCreateModalVisible = false;
        },
        showCreateModal (event) {
            if (!gon?.current_user_id) {
                // If user is signed out, don't show modal, but allow them to click on the button to sign in
                return;
            }
            if (Boolean(event) && isMetaClick(event)) {
                // opening in a new tab
                return;
            }
            // don't follow the link for normal clicks - open in modal
            event?.preventDefault();
            this.isCreateModalVisible = true;
        },
        hideConfirmationModal () {
            this.isConfirmationModalVisible = false;
        },
        showConfirmationModal () {
            this.isConfirmationModalVisible = true;
        },
        /*
     Beginning of the methods for the confirmation modal when enabled

     The confirmation modal is enabled when any form field is
     filled or different from the default value.
    */ handleConfirmCancellation () {
            this.showConfirmationModal();
        },
        handleContinueEditing () {
            this.shouldDiscardDraft = false;
            this.hideConfirmationModal();
        },
        handleDiscardDraft (modal) {
            if (modal === 'createModal') {
                // This is triggered on the create modal when the user didn't update the form,
                // so we just hide the create modal as there's no draft to discard
                this.hideCreateModal();
            } else {
                // This is triggered on the confirmation modal, so the user updated the form and
                // we want to trigger discard draftfunction on create work item component because
                // the user confirmed it
                this.shouldDiscardDraft = true;
                this.hideConfirmationModal();
                this.hideCreateModal();
            }
        },
        /*
    End of the methods for the confirmation modal when enabled
    */ handleCreated ({ workItem }) {
            if (!this.suppressCreatedToast) {
                const createdWorkItemTypeName = workItem?.workItemType?.name || this.selectedWorkItemTypeName;
                const workItemCreatedText = sprintf(s__('WorkItem|%{workItemType} created.'), {
                    workItemType: createdWorkItemTypeName
                });
                this.$toast.show(workItemCreatedText, {
                    autoHideDelay: 10000,
                    action: {
                        text: __('View details'),
                        href: workItem.webUrl,
                        onClick: (e)=>{
                            e?.preventDefault();
                            // Take incidents to the legacy detail view with a full page load
                            if (this.useVueRouter && workItem?.workItemType?.name !== WORK_ITEM_TYPE_NAME_INCIDENT && this.$router.getRoutes().some((route)=>route.name === 'workItem') && canRouterNav({
                                fullPath: this.fullPath,
                                isGroup: this.isGroup,
                                webUrl: workItem.webUrl,
                                issueAsWorkItem: true
                            })) {
                                this.$router.push({
                                    name: 'workItem',
                                    params: {
                                        iid: workItem.iid,
                                        type: WORK_ITEM_TYPE_ROUTE_WORK_ITEM
                                    }
                                });
                            } else {
                                visitUrl(workItem.webUrl);
                            }
                        }
                    }
                });
            }
            this.$emit('work-item-created', workItem);
            this.hideCreateModal();
        },
        redirectToNewPage (event) {
            event.preventDefault();
            if (this.useVueRouter) {
                this.$router.push({
                    name: ROUTES.new,
                    query: {
                        [RELATED_ITEM_ID_URL_QUERY_PARAM]: this.relatedItem?.id,
                        type: this.selectedWorkItemTypeName,
                        initialCreationContext: this.creationContext
                    }
                });
            } else {
                visitUrl(this.newWorkItemPath);
            }
        }
    }
};
